<template>
  <!-- Tabla manual reutilizable: edición al clic por celda + alta/baja de filas. -->
  <div class="editable-table" :class="{ mark, scroll }">
    <table>
      <thead v-if="!hideHeader">
        <tr>
          <th v-if="numbered" class="num-col">{{ numberLabel }}</th>
          <th v-for="col in columns" :key="col.key" :style="colStyle(col)">
            <span class="th-label">{{ col.label }}</span>
            <source-chip v-if="mark && col.src" :source="col.src" :short="true"></source-chip>
          </th>
          <th class="actions-col" :class="{ labeled: actionsLabel }">{{ actionsLabel }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, i) in localRows" :key="row._k" :class="rowClass ? rowClass(row) : ''">
          <td v-if="numbered" class="num-col"><span class="row-num">{{ i + 1 }}</span></td>
          <td v-for="col in columns" :key="col.key" :style="{ textAlign: col.align || 'left' }">
            <!-- Solo lectura (p. ej. score calculado) -->
            <span v-if="col.type === 'readonly'" class="readonly" :class="col.cellClass ? col.cellClass(row) : ''">{{ col.format ? col.format(row) : row[col.key] }}</span>
            <!-- Editable al clic (texto, fecha o lista). El formato (p. ej. €) se muestra en reposo; al editar se ve el valor crudo. -->
            <prm-summary-manual-field v-else
              :value="row[col.key]"
              :type="col.type === 'date' ? 'date' : 'text'"
              :options="col.type === 'select' ? col.options : null"
              :format="col.format || null"
              :sub="col.sub || null"
              :block="col.type !== 'select'"
              :multiline="!!col.multiline"
              :view-class="col.cellClass ? col.cellClass(row) : ''"
              @save="onCellSave(i, col.key, $event)">
            </prm-summary-manual-field>
          </td>
          <td class="actions-col" :class="{ labeled: actionsLabel }">
            <button class="row-remove" v-tip="removeTitle" @click="removeRow(i)">
              <b-icon pack="fas" icon="trash-can" size="is-small"></b-icon>
            </button>
          </td>
        </tr>
        <tr v-if="!localRows.length">
          <td :colspan="columns.length + (numbered ? 2 : 1)" class="empty">{{ emptyText }}</td>
        </tr>
      </tbody>
    </table>

    <div v-if="!hideFooter" class="tbl-footer">
      <button class="row-add" :disabled="atLimit" @click="addRow">
        <b-icon pack="fas" icon="plus" size="is-small"></b-icon> {{ addLabel }}
      </button>
      <span v-if="atLimit" class="limit-hint">{{ limitHint }}</span>
    </div>
  </div>
</template>

<script>
  import PrmSummaryManualField from './manual-field.vue'
  import SourceChip from './source-chip.vue'
  import { tip } from '../shared/tip'

  let keySeq = 0

  export default {
    name: 'PrmSummaryEditableTable',

    components: { PrmSummaryManualField, SourceChip },

    directives: { tip },

    props: {
      rows: { type: Array, default: () => [] },
      columns: { type: Array, required: true }, // [{ key, label, type, options, align, width, format, cellClass, multiline }]
      blank: { type: Object, default: () => ({}) }, // plantilla de fila nueva
      addLabel: { type: String, default: '+' },
      removeTitle: { type: String, default: '' },
      emptyText: { type: String, default: '' },
      limitHint: { type: String, default: '' },
      maxRows: { type: Number, default: 0 }, // 0 = sin límite
      resetKey: { type: Number, default: 0 }, // cambia al recargar → re-inicializa la copia local
      rowClass: { type: Function, default: null },
      mark: { type: Boolean, default: false }, // tabla sin fuente: tinte ámbar + subrayado en celdas
      numbered: { type: Boolean, default: false }, // numera las filas (1, 2, 3...)
      numberLabel: { type: String, default: '#' }, // cabecera de la columna de numeración
      hideHeader: { type: Boolean, default: false }, // oculta la cabecera (lista numerada simple)
      scroll: { type: Boolean, default: false }, // scroll horizontal (tablas muy anchas)
      hideFooter: { type: Boolean, default: false }, // oculta el "Añadir fila" del pie (se usa uno arriba)
      actionsLabel: { type: String, default: '' }, // etiqueta de la columna de acciones (p. ej. "Acciones")
    },

    data () {
      return { localRows: [] }
    },

    computed: {
      atLimit () {
        return this.maxRows > 0 && this.localRows.length >= this.maxRows
      },
    },

    watch: {
      // Solo re-inicializamos con una recarga real (no en cada emisión), para no perder foco.
      resetKey: { immediate: true, handler () { this.initLocal() } },
    },

    methods: {
      initLocal () {
        this.localRows = (this.rows || []).map((r) => ({ ...r, _k: ++keySeq }))
      },
      colStyle (col) {
        return { textAlign: col.align || 'left', width: col.width || 'auto' }
      },
      onCellSave (i, key, value) {
        this.localRows[i][key] = value
        this.emit()
      },
      addRow () {
        if (this.atLimit) return
        this.localRows.push({ ...this.clone(this.blank), _k: ++keySeq })
        this.emit()
      },
      removeRow (i) {
        this.localRows.splice(i, 1)
        this.emit()
      },
      clone (obj) {
        return JSON.parse(JSON.stringify(obj))
      },
      // Emite el array limpio (sin la clave interna _k) al padre, que lo persiste.
      emit () {
        const clean = this.localRows.map((r) => {
          const row = { ...r }
          delete row._k
          return row
        })
        this.$emit('change', clean)
      },
    },
  }
</script>

<style lang="scss" scoped>
  .editable-table { min-width: 0; }
  // Tablas muy anchas (hitos de pago): scroll horizontal sin aplastar columnas.
  .editable-table.scroll { overflow-x: auto; }
  .editable-table.scroll table { width: auto; min-width: 100%; }
  .editable-table.scroll th, .editable-table.scroll td { white-space: nowrap; }
  table { width: 100%; border-collapse: collapse; }
  th {
    background: #3d6b9a; color: #fff; font-size: 9px; text-transform: uppercase; letter-spacing: .025em;
    font-weight: 700; padding: 6px 8px; text-align: left; white-space: nowrap;
  }
  // Chip de responsable en la cabecera (modo fuente): bajo la etiqueta de columna.
  th .th-label { display: block; }
  th .src-chip { margin: 3px 0 0; }
  td { border-bottom: 1px solid #eef0f3; padding: 6px 8px; font-size: 10px; color: #1f2837; vertical-align: middle; }
  td.empty { text-align: center; color: #9aa4b2; padding: 12px; }
  .actions-col { width: 32px; text-align: center; }
  .actions-col.labeled { width: 80px; }
  .num-col { width: 34px; text-align: center; }
  .row-num {
    display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px;
    border-radius: 50%; background: #e9edf2; color: #3d5570; font-size: 9px; font-weight: 700;
  }
  .readonly { font-weight: 600; }

  .row-remove { border: 0; background: transparent; color: #c0392b; cursor: pointer; opacity: .55; }
  .row-remove:hover { opacity: 1; }

  .tbl-footer { padding: 8px 10px; }
  .row-add {
    border: 1px solid #9fb5cc; background: #fff; color: #2f5f8f;
    font-size: 10px; font-weight: 600; padding: 4px 10px; border-radius: 4px; cursor: pointer;
  }
  .row-add:hover:not(:disabled) { background: #f4f8fc; }
  .row-add:disabled { opacity: .5; cursor: not-allowed; }
  .limit-hint { margin-left: 8px; font-size: 11px; color: #9aa4b2; }

  // Hover para revisar (sin cebra: el diseño usa filas limpias); no pisa criticidad ni ámbar.
  tbody tr:hover:not(.red):not(.amber):not(.green) td { background: #f3f6fa; }

  // Tabla sin fuente: tinte ámbar en las filas del cuerpo (como el diseño), con cebra/hover ámbar.
  .editable-table.mark tbody td { background: #fff8e6; }
  .editable-table.mark tbody tr:nth-child(even):not(.red):not(.amber):not(.green) td { background: #fdf3d0; }
  .editable-table.mark tbody tr:hover:not(.red):not(.amber):not(.green) td { background: #fbecc0; }

  // Coloreado de fila según criticidad (S05): prevalece sobre el tinte de sin fuente.
  tr.red td { background: #fdecea; }
  tr.amber td { background: #fdf7e6; }
  tr.green td { background: #eaf7ee; }
  tr.red:hover td { background: #fbddd8; }
  tr.amber:hover td { background: #f8eecb; }
  tr.green:hover td { background: #ddf0e3; }
</style>
